import { useState, useEffect, useMemo } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Columns3, TriangleAlert, FileText, MessageSquare,
  Building2, BarChart3, Settings, CreditCard, Ship, Search, Bell, LogOut, ChevronDown, Menu, X, CheckCircle2, Clock, ListChecks, ShieldCheck, Eye, Pencil,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { needsAttention } from "@/lib/format";
import client from "@/lib/api";
import { MovaraMark } from "@/components/Brand";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogFooter, AlertDialogTitle, AlertDialogDescription,
  AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const NAV_GROUPS = [
  {
    title: "Daily Control",
    items: [
      { to: "/app/today", label: "Today", icon: LayoutDashboard },
      { to: "/app/my-queue", label: "My Queue", icon: ListChecks },
    ],
  },
  {
    title: "Freight Operations",
    items: [
      { to: "/app/board", label: "Dispatch Board", icon: Columns3 },
      { to: "/app/needs-attention", label: "Hot Shipments", icon: TriangleAlert, badge: "hot" },
      { to: "/app/documents", label: "Manifests & PODs", icon: FileText, badge: "docs" },
      { to: "/app/customer-updates", label: "Broker Check-Ins", icon: MessageSquare },
      { to: "/app/customers", label: "Customers", icon: Building2 },
    ],
  },
  {
    title: "Management",
    items: [
      { to: "/app/reports", label: "Reports", icon: BarChart3 },
      { to: "/app/billing", label: "Billing", icon: CreditCard },
      { to: "/app/settings", label: "Settings", icon: Settings },
    ],
  },
];

// Subtle per-section canvas tints so each workspace area feels distinct without
// bright colors. All stay within a calm light gray/slate range.
const SECTION_BG = {
  today: "#E5EBF2",
  "my-queue": "#E7EAEF",
  board: "#DEE4EC",
  "needs-attention": "#E9ECF1",
  documents: "#E2E8EF",
  "customer-updates": "#E0E7F1",
  customers: "#E6E9EE",
  reports: "#DBE2EB",
  settings: "#E8EBEF",
  billing: "#DEE4EC",
  "inter-island": "#DCE8E4",
  shipments: "#E3E9EF",
  admin: "#E1E6ED",
};
function sectionBg(pathname) {
  const seg = pathname.replace(/^\/app\//, "").split("/")[0];
  return SECTION_BG[seg] || "#EEF1F4";
}

export default function Layout({ children, onSearch }) {
  const { user, logout } = useAuth();
  const { workspace, exitWorkspace, enterWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [maritime, setMaritime] = useState(!!workspace?.enable_interisland_transit);

  const isSuper = user?.role === "super_admin";
  const onAdminRoute = location.pathname.startsWith("/app/admin");
  const inWorkspaceView = isSuper && !!workspace && !onAdminRoute;
  const isDemoWs = workspace?.id === "movora";
  const needsWorkspace = isSuper && !workspace && !onAdminRoute;
  const [resetting, setResetting] = useState(false);

  const resetDemo = async () => {
    setResetting(true);
    try {
      await client.post("/admin/demo/reset");
      toast.success("Demo data reset.");
      setTimeout(() => window.location.reload(), 700);
    } catch (e) {
      setResetting(false);
      toast.error("Could not reset demo data.");
    }
  };

  useEffect(() => {
    if (isSuper && !workspace) return;
    // Seed region from the cached workspace so the correct data tables render
    // immediately, then confirm from /meta.
    if (workspace?.enable_interisland_transit != null) setMaritime(!!workspace.enable_interisland_transit);
    client.get("/shipments").then((r) => setJobs(r.data)).catch(() => {});
    client.get("/meta").then((r) => setMaritime(!!r.data.enable_interisland_transit)).catch(() => {});
  }, [isSuper, workspace]);

  const navGroups = useMemo(() => {
    if (!maritime) return NAV_GROUPS;
    return NAV_GROUPS.map((g) =>
      g.title === "Freight Operations"
        ? {
            ...g,
            items: [
              ...g.items.map((it) =>
                it.to === "/app/documents" ? { ...it, label: "Delivery Verification" } : it
              ),
              { to: "/app/inter-island", label: "Inter-Island Ports", icon: Ship },
            ],
          }
        : g
    );
  }, [maritime]);

  const exitToAdmin = () => {
    exitWorkspace();
    navigate("/app/admin");
  };

  const notifications = useMemo(() => {
    const out = [];
    const N = { "missing-docs": FileText, cu: MessageSquare, na: TriangleAlert, rtc: CheckCircle2 };
    jobs.forEach((j) => {
      if (j.current_stage === "Closed") return;
      if ((j.issues || []).some((i) => i.startsWith("Missing documents")))
        out.push({ id: `${j.id}-md`, icon: N["missing-docs"], color: "#DC2626", job: j, label: "Missing documents", focus: "documents" });
      if ((j.issues || []).includes("Customer update overdue"))
        out.push({ id: `${j.id}-cu`, icon: N.cu, color: "#D97706", job: j, label: "Customer update due", focus: "customer-updates" });
      if ((j.issues || []).includes("Next action overdue"))
        out.push({ id: `${j.id}-na`, icon: N.na, color: "#D97706", job: j, label: "Overdue next action", focus: "controls" });
      if (j.risk_status === "Delayed")
        out.push({ id: `${j.id}-dl`, icon: N.na, color: "#DC2626", job: j, label: "Shipment delayed", focus: "controls" });
      if (j.ready_to_close)
        out.push({ id: `${j.id}-rtc`, icon: N.rtc, color: "#059669", job: j, label: "Ready to close", focus: "controls" });
    });
    return out.slice(0, 15);
  }, [jobs]);

  const hotCount = useMemo(
    () => jobs.filter((j) => j.current_stage !== "Closed" && needsAttention(j)).length,
    [jobs]
  );
  const docsCount = useMemo(
    () => jobs.filter((j) => j.current_stage !== "Closed" && (j.issues || []).some((i) => i.startsWith("Missing documents"))).length,
    [jobs]
  );

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long", month: "short", day: "numeric",
  });

  const initials = (user?.name || "U")
    .split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-app-bg text-ink">
      {/* Mobile overlay */}
      {open && (
        <div className="fixed inset-0 bg-black/40 z-40 md:hidden" onClick={() => setOpen(false)} data-testid="sidebar-overlay" />
      )}

      {/* Sidebar */}
      <aside
        className={`w-60 bg-sidebar text-white flex flex-col fixed inset-y-0 left-0 z-50 transform transition-transform duration-200 ${
          open ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0`}
      >
        <div className="h-14 flex items-center justify-between px-5 border-b border-white/10">
          <div className="flex items-center gap-2.5 text-white">
            <MovaraMark className="h-5 w-6" />
            <span className="font-heading font-semibold tracking-tight text-[15px]">MovaRaBoard</span>
          </div>
          <button className="md:hidden text-white/60 hover:text-white" onClick={() => setOpen(false)} data-testid="sidebar-close-btn">
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="flex-1 py-4 px-3 mv-scroll overflow-y-auto">
          {navGroups.map((group) => (
            <div key={group.title} className="mb-4">
              <span className="px-3 text-[10px] font-mono font-bold tracking-widest text-white/40 uppercase block mb-2">
                {group.title}
              </span>
              <div className="space-y-1">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const badgeCount = item.badge === "hot" ? hotCount : item.badge === "docs" ? docsCount : 0;
                  const badgeColor = item.badge === "docs" ? "bg-[#DC2626]" : "bg-[#D97706]";
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setOpen(false)}
                      data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                      className={({ isActive }) =>
                        `flex items-center justify-between gap-2 px-3 py-2 rounded-md text-[13.5px] transition-colors duration-150 ${
                          isActive
                            ? "bg-white/[0.12] text-white font-bold"
                            : "text-white/60 hover:text-white hover:bg-white/[0.06] font-semibold"
                        }`
                      }
                    >
                      <span className="flex items-center gap-3">
                        <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
                        {item.label}
                      </span>
                      {item.badge && badgeCount > 0 && (
                        <span
                          data-testid={`nav-badge-${item.badge}`}
                          className={`${badgeColor} text-white text-[10px] font-black px-1.5 py-0.5 rounded-md`}
                        >
                          {badgeCount}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
        <div className="border-t border-white/10">
          {user?.role === "super_admin" && (
            <div className="px-4 py-3 bg-[#060B18]">
              <span className="text-[9px] font-mono font-bold text-white/40 tracking-wider uppercase block">Platform Access</span>
              <NavLink
                to="/app/admin"
                onClick={() => setOpen(false)}
                data-testid="nav-admin-console"
                className={({ isActive }) =>
                  `flex items-center gap-2 mt-1.5 text-xs font-semibold transition-colors ${
                    isActive ? "text-white" : "text-white/60 hover:text-white"
                  }`
                }
              >
                <ShieldCheck className="h-4 w-4" strokeWidth={1.75} /> Admin Console
              </NavLink>
            </div>
          )}
          <div className="px-4 py-3 text-[11px] text-white/35 font-mono-data">
            v1.0 · Operations Build
          </div>
        </div>
      </aside>

      {/* Main */}
      <div style={{ backgroundColor: sectionBg(location.pathname) }} className="md:ml-60 flex flex-col min-h-screen transition-colors duration-300">
        {/* Top bar */}
        <header className="h-14 bg-sidebar border-b border-white/10 flex items-center gap-3 px-4 md:px-6 sticky top-0 z-30">
          <button className="md:hidden text-white/70 hover:text-white shrink-0" onClick={() => setOpen(true)} data-testid="sidebar-open-btn">
            <Menu className="h-5 w-5" />
          </button>
          <div className="md:hidden flex items-center gap-2 text-white">
            <MovaraMark className="h-5 w-6" />
            <span className="font-heading font-semibold text-[14px] text-white">MovaRaBoard</span>
          </div>
          <div className="relative flex-1 max-w-md hidden md:block">
            <Search className="h-4 w-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="global-search-input"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                onSearch && onSearch(e.target.value);
              }}
              placeholder="Search job number, customer, location…"
              className="w-full h-9 pl-9 pr-3 rounded-sm border border-white/15 bg-white/10 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-white/30 focus:border-white/30 focus:bg-white/[0.14] transition-colors"
            />
          </div>
          <div className="flex items-center gap-3 md:gap-4 ml-auto">
            <span className="text-[13px] text-white/55 hidden lg:block font-mono-data">{today}</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="notifications-btn" className="relative text-white/70 hover:text-white transition-colors">
                  <Bell className="h-[18px] w-[18px]" strokeWidth={1.75} />
                  {notifications.length > 0 && <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-danger" />}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 p-0">
                <div className="px-3.5 py-2.5 border-b border-line flex items-center justify-between">
                  <span className="op-label">Notifications</span>
                  <span className="font-mono-data text-[11px] text-ink-3">{notifications.length}</span>
                </div>
                <div className="max-h-[360px] overflow-y-auto mv-scroll">
                  {notifications.length === 0 ? (
                    <div className="px-3.5 py-8 text-center text-[13px] text-ink-3">You're all caught up.</div>
                  ) : (
                    notifications.map((n) => {
                      const Icon = n.icon;
                      return (
                        <button
                          key={n.id}
                          data-testid={`notification-${n.id}`}
                          onClick={() => navigate(`/app/shipments/${n.job.id}?focus=${n.focus}`)}
                          className="w-full text-left px-3.5 py-2.5 hover:bg-surface-alt transition-colors flex items-start gap-2.5 border-b border-line last:border-0"
                        >
                          <Icon className="h-4 w-4 mt-0.5 shrink-0" style={{ color: n.color }} strokeWidth={1.75} />
                          <div className="min-w-0">
                            <div className="text-[13px] text-ink font-medium">{n.label}</div>
                            <div className="text-[11.5px] text-ink-2 truncate">
                              <span className="font-mono-data">{n.job.job_number}</span> · {n.job.customer_name}
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-btn" className="flex items-center gap-2 hover:bg-white/10 rounded-sm pl-1 pr-2 py-1 transition-colors">
                  <span className="h-7 w-7 rounded-sm bg-steel text-white text-xs font-medium flex items-center justify-center">{initials}</span>
                  <div className="text-left hidden sm:block leading-tight">
                    <div className="text-[13px] font-medium text-white">{user?.name}</div>
                    <div className="text-[11px] text-white/50 capitalize">{user?.role}</div>
                  </div>
                  <ChevronDown className="h-3.5 w-3.5 text-white/50" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="text-xs text-ink-3 font-normal">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/app/settings")} data-testid="menu-settings">
                  <Settings className="h-4 w-4 mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-danger focus:text-danger">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {inWorkspaceView && (
          isDemoWs ? (
            <div data-testid="demo-test-banner" className="bg-brand/[0.08] border-b border-brand/25 px-4 md:px-6 py-2 flex items-center gap-3 text-[12.5px]">
              <ListChecks className="h-4 w-4 text-brand shrink-0" strokeWidth={1.9} />
              <span className="text-ink-2">
                Demo Test Workspace — you are editing <span className="font-medium text-ink">MovaraBoard Demo</span> sample data.
              </span>
              <div className="ml-auto flex items-center gap-2 shrink-0">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid="reset-demo-btn" disabled={resetting}
                      className="h-7 px-3 rounded-sm border border-brand/40 text-brand text-[12px] font-medium hover:bg-brand/10 transition-colors disabled:opacity-50">
                      {resetting ? "Restoring…" : "Reset demo data"}
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="reset-demo-dialog">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Reset MovaraBoard Demo?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will restore the demo workspace to the clean sample baseline and remove any test changes made during review. This only affects MovaraBoard Demo.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="reset-demo-cancel-btn">Cancel</AlertDialogCancel>
                      <AlertDialogAction data-testid="reset-demo-confirm-btn" onClick={resetDemo}>
                        Reset Demo Data
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                <button data-testid="exit-workspace-btn" onClick={exitToAdmin}
                  className="h-7 px-3 rounded-sm border border-line text-ink-2 text-[12px] font-medium hover:text-ink hover:border-steel transition-colors">
                  Exit
                </button>
              </div>
            </div>
          ) : (
            <div data-testid="workspace-preview-bar" className="sticky top-14 z-20 bg-[#D97706] border-b-2 border-[#0B132B] px-4 md:px-6 py-2.5 flex items-center gap-3 text-[12.5px] shadow-[0_2px_0px_0px_#0B132B]">
              <Pencil className="h-4 w-4 text-white shrink-0" strokeWidth={2.2} />
              <span className="text-white font-semibold">
                Live edit mode — you are editing <span className="font-black underline decoration-white/60">{workspace.name}</span>. Changes save directly to this client's workspace.
              </span>
              <button data-testid="exit-preview-save-btn" onClick={exitToAdmin}
                className="ml-auto shrink-0 h-9 px-4 rounded-md bg-white text-[#0B132B] text-[12.5px] font-black uppercase tracking-wide border-2 border-[#0B132B] shadow-[2px_2px_0px_0px_#0B132B] hover:bg-[#FFF7ED] active:translate-y-0.5 active:translate-x-0.5 active:shadow-none transition-all">
                🚪 EXIT PREVIEW &amp; SAVE MODULE
              </button>
            </div>
          )
        )}

        {needsWorkspace ? (
          <main className="flex-1 p-4 md:p-6 max-w-[1500px] w-full overflow-x-hidden">
            <div data-testid="select-workspace-prompt" className="bg-surface border border-line rounded-md p-12 text-center max-w-lg mx-auto mt-8">
              <Building2 className="h-7 w-7 text-steel mx-auto mb-3" strokeWidth={1.75} />
              <h2 className="font-heading text-lg font-medium text-ink">Select a workspace to view</h2>
              <p className="text-sm text-ink-2 mt-1.5">Choose how you want to review the platform. Both the demo and real client workspaces open in full live edit mode — changes save to that workspace.</p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center mt-5">
                <button data-testid="enter-demo-workspace-btn" onClick={() => { exitWorkspace(); enterWorkspace("movora", "MovaraBoard Demo", "CARIBBEAN_MARITIME", true); navigate("/app/today"); }}
                  className="h-9 px-4 rounded-sm bg-brand text-white text-sm font-medium hover:bg-brand-dark inline-flex items-center justify-center gap-1.5">
                  <ListChecks className="h-4 w-4" /> Enter MovaraBoard Demo Workspace
                </button>
                <button data-testid="view-client-readonly-btn" onClick={() => navigate("/app/admin")}
                  className="h-9 px-4 rounded-sm border border-line text-ink-2 text-sm font-medium hover:border-steel hover:text-ink inline-flex items-center justify-center gap-1.5">
                  <Eye className="h-4 w-4" /> Open a Client Workspace
                </button>
              </div>
              <p className="text-[11.5px] text-ink-3 mt-3">Enter any workspace to add jobs, upload delivery proofs, and edit live before hitting Deploy.</p>
            </div>
          </main>
        ) : (
          <main data-testid="workspace-main" className="flex-1 p-4 md:p-6 max-w-[1500px] w-full overflow-x-hidden">{children}</main>
        )}
      </div>
    </div>
  );
}
